/**
 * Reconnecting client for the active transport's notifications URL.
 *
 * ONE stream serves every subscriber. It has to be shared: cloud delivers this
 * as Server-Sent Events, and a browser allows only six connections per host on
 * HTTP/1.1 - so a stream per subscriber fills the pool with idle listeners and every
 * ordinary fetch after that queues forever. The symptom is not an error: the
 * device list and the archive segments simply never arrive.
 *
 * The connection opens with the first subscriber and closes with the last. The
 * server sends every current status once, when the stream opens, so a
 * subscriber arriving later is handed the latest status of every device and
 * server the stream has reported - and nothing else: an alert is for whoever
 * was listening when it fired.
 *
 * Each frame is parsed at the boundary.
 *
 * Usage:
 *   const unsub = subscribeNotifications((event) => { ... });
 *   // later:
 *   unsub();
 */

import { z } from 'zod';

import { authSession } from '@/auth/authSession';
import { SECOND_MS } from '@/lib/timeUnits';
import type { Unsubscribe } from '@/types/common';
import { type ViolationSource, ViolationSourceKind } from '@/types/contract';
import {
    DEVICE_STATUS_KEY_PREFIX, IO_STATE_KEY_PREFIX, type NotificationEvent,
    type NotificationListener, SERVER_STATUS_KEY_PREFIX, StreamEvent, type StreamEventListener,
    type StreamInput, StreamInputKind,
} from '@/types/notifications';
import { PortalEvent } from '@/types/portal';

import { parseSourcedFrame } from './contract';
import { api } from './index';
import { portalMonitor } from './portalMonitor';
import { CloudFrameSchema } from './transformers/notifications';

interface Registry {
    listeners: Set<NotificationListener>;
    connector: NotificationConnector | null;
    /** Latest status per key, replayed to a subscriber that arrives later. */
    statuses: Map<string, NotificationEvent>;
    streamListeners: Set<StreamEventListener>;
    /** The last stream event, replayed to a subscriber that arrives later; null before any. */
    streamState: StreamEvent | null;
}

// Keeps a dropped stream from reconnecting in a tight loop against a down server.
const RECONNECT_DELAY_MS = 3 * SECOND_MS;

// One connection per PAGE, not per module generation. Vite replaces this
// module on every hot update, and a fresh module brings fresh state: its first
// subscriber would open a second stream beside the one the previous generation
// still holds, and a long dev session would drift back into the starvation the
// sharing exists to prevent. The registry lives on globalThis, which a module
// reload cannot reach.
const REGISTRY_KEY = Symbol.for('occurra.notifications');

/**
 * How long the stream may be without a connection before it reports itself down. A drop that
 * recovers sooner is a glitch and reports nothing. Everything that means "the stream is gone"
 * listens for StreamEvent.Down rather than measuring this again.
 */
export const STREAM_DOWN_GRACE_MS = 10 * SECOND_MS;

/** The frames a media_server builds, which the cloud forwards without looking inside. */
const ForwardedFrameSchema = z.object({
    type: z.enum(['ruleNotification']),
    serverId: z.string().min(1),
});

/** A frame a media_server built is that server's, graded by its update state. */
function getCloudFrameSource(frame: unknown): ViolationSource {
    const forwarded = ForwardedFrameSchema.safeParse(frame);
    if (!forwarded.success) {
        return { kind: ViolationSourceKind.Cloud };
    }
    return {
        kind: ViolationSourceKind.Relay,
        serverId: forwarded.data.serverId,
    };
}

/** What a status frame is the latest word on; null for any other frame. */
function getStatusKey(event: NotificationEvent): string | null {
    switch (event.type) {
        case 'deviceStatusChanged':
            return `${DEVICE_STATUS_KEY_PREFIX}${event.deviceId}`;
        case 'serverStatusChanged':
            return `${SERVER_STATUS_KEY_PREFIX}${event.serverId}`;
        case 'ioStateChanged':
            return `${IO_STATE_KEY_PREFIX}${event.deviceId}`;
        default:
            return null;
    }
}

/**
 * The single stream's connection, from `start()` to `stop()`: it reconnects after a drop, reports
 * the stream open or down, and follows the active organization.
 */
class NotificationConnector {
    private readonly registry: Registry;

    private stream: EventSource | null = null;

    /** The path of the stream opened last, which names it in a contract violation. */
    private endpoint = '';

    private isStopped = false;

    private reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    private graceTimer: ReturnType<typeof setTimeout> | undefined;

    private activeOrg = authSession.getActiveOrg();

    private unsubscribeAuth: Unsubscribe | null = null;

    private unsubscribePortal: Unsubscribe | null = null;

    /**
     * True from an outage of the cloud until a connection opens: the stream can hang on a dead
     * connection that the browser does not notice.
     */
    private mayBeDead = false;

    constructor(registry: Registry) {
        this.registry = registry;
    }

    start(): void {
        this.connect();
        this.markLost();
        // The status stream is org-scoped, so without this it would keep streaming the previous
        // org's statuses (or nothing) after a switch.
        this.unsubscribeAuth = authSession.subscribe((): void => this.onAuthChange());
        this.unsubscribePortal = portalMonitor.subscribe(
            (event): void => this.onPortalEvent(event),
        );
    }

    stop(): void {
        this.isStopped = true;
        this.unsubscribeAuth?.();
        this.unsubscribeAuth = null;
        this.unsubscribePortal?.();
        this.unsubscribePortal = null;
        this.teardown();
        clearTimeout(this.graceTimer);
        this.registry.streamState = null;
    }

    private onAuthChange(): void {
        const org = authSession.getActiveOrg();
        if (org === this.activeOrg) {
            return;
        }
        this.activeOrg = org;
        this.teardown();
        // What the stream said about the previous organization says nothing about this one.
        clearTimeout(this.graceTimer);
        this.graceTimer = undefined;
        this.registry.streamState = null;
        this.connect();
        this.markLost();
    }

    private onPortalEvent(event: PortalEvent): void {
        switch (event) {
            case PortalEvent.Unavailable:
                this.mayBeDead = true;
                break;
            case PortalEvent.Recovered:
                if (this.mayBeDead) {
                    this.reconnect();
                }
                break;
            default:
                break;
        }
    }

    private reconnect(): void {
        this.teardown();
        this.connect();
        this.markLost();
    }

    private connect(): void {
        if (this.isStopped) {
            return;
        }
        // Resolve the URL per-connect: the cloud notifications URL is org-scoped
        // (?orgId=, read from getActiveOrg() on every call), so a reconnect after
        // an org switch must target the new org. If the transport doesn't support
        // notifications yet (e.g. cloud with no active org), stay idle — an org
        // change reconnects through onAuthChange.
        let url: string;
        try { url = api.getNotificationsUrl(); } catch { return; }
        this.endpoint = new URL(url).pathname;
        this.stream = this.openEventSource(url);
    }

    /** The cloud SSE stream; rides HTTP/2, and EventSource reconnects a transient drop itself. */
    private openEventSource(url: string): EventSource {
        const es = new EventSource(url, { withCredentials: true });
        es.onopen = (): void => this.markOpen();
        es.onmessage = (event): void => this.handleFrame(event.data);
        es.onerror = (): void => {
            this.markLost();
            // EventSource self-retries a transient drop (readyState CONNECTING), so
            // leave those to it. It does NOT retry a fatal error like a 401
            // (readyState CLOSED) — which happens when the stream opens before the
            // session cookie is set at page load. Reconnect those manually after a
            // short delay so the stream self-heals instead of staying dead.
            if (es.readyState === EventSource.CLOSED && !this.isStopped) {
                this.teardown();
                this.scheduleReconnect();
            }
        };
        return es;
    }

    private scheduleReconnect(): void {
        this.reconnectTimer = setTimeout(() => this.connect(), RECONNECT_DELAY_MS);
    }

    private teardown(): void {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = undefined;
        // The stream that comes next sends every status afresh - for the org it is
        // opened for, which is not always this one.
        this.registry.statuses.clear();
        if (!this.stream) {
            return;
        }
        // Drop handlers first so a teardown-triggered close/error can neither
        // schedule a reconnect nor surface a late frame after we move on.
        this.stream.onopen = null;
        this.stream.onmessage = null;
        this.stream.onerror = null;
        this.stream.close();
        this.stream = null;
    }

    private markOpen(): void {
        // Cleared first: the report below can end the outage and ask for a reconnect.
        this.mayBeDead = false;
        clearTimeout(this.graceTimer);
        this.graceTimer = undefined;
        this.announce(StreamEvent.Open);
        portalMonitor.reportStreamOpen();
    }

    // Starts the grace period, unless one is already running or has already run out.
    private markLost(): void {
        if (this.graceTimer !== undefined || this.registry.streamState === StreamEvent.Down) {
            return;
        }
        this.graceTimer = setTimeout(() => {
            this.graceTimer = undefined;
            this.announce(StreamEvent.Down);
            portalMonitor.reportStreamDown();
        }, STREAM_DOWN_GRACE_MS);
    }

    private announce(event: StreamEvent): void {
        this.registry.streamState = event;
        for (const listener of [...this.registry.streamListeners]) {
            try { listener(event); } catch { /* one subscriber must not stop the rest */ }
        }
    }

    private handleFrame(data: string): void {
        const event = this.getEvent(data);
        if (event !== null) {
            this.deliver(event);
        }
    }

    /** The cloud's stream also carries its status frames and frames other senders built. */
    private getEvent(data: string): NotificationEvent | null {
        return parseSourcedFrame(CloudFrameSchema, data, {
            endpoint: this.endpoint,
            source: { kind: ViolationSourceKind.Cloud },
        }, getCloudFrameSource);
    }

    // Fans every frame out to whoever is subscribed WHEN IT ARRIVES - which
    // includes subscribers a later module generation added to the same registry.
    private deliver(event: NotificationEvent): void {
        const statusKey = getStatusKey(event);
        if (statusKey) {
            this.registry.statuses.set(statusKey, event);
        }
        for (const listener of [...this.registry.listeners]) {
            try { listener(event); } catch { /* one subscriber must not stop the rest */ }
        }
    }
}

function getRegistry(): Registry {
    const host = globalThis as { [REGISTRY_KEY]?: Registry };
    let registry = host[REGISTRY_KEY];
    if (!registry) {
        registry = {
            listeners: new Set(),
            connector: null,
            statuses: new Map(),
            streamListeners: new Set(),
            streamState: null,
        };
        host[REGISTRY_KEY] = registry;
    }
    return registry;
}

export function subscribeNotifications(onMessage: NotificationListener): Unsubscribe {
    const registry = getRegistry();
    registry.listeners.add(onMessage);
    if (!registry.connector) {
        registry.connector = new NotificationConnector(registry);
        registry.connector.start();
    }
    for (const event of [...registry.statuses.values()]) {
        try { onMessage(event); } catch { /* the subscriber's own problem */ }
    }
    return () => {
        registry.listeners.delete(onMessage);
        if (registry.listeners.size > 0 || !registry.connector) {
            return;
        }
        registry.connector.stop();
        registry.connector = null;
    };
}

/**
 * Tells `onEvent` whenever the stream opens a connection or has been down past its grace
 * period, starting with the latest of the two if either has happened. It does not open the
 * stream: that is subscribeNotifications' job.
 */
export function subscribeStreamEvents(onEvent: StreamEventListener): Unsubscribe {
    const registry = getRegistry();
    registry.streamListeners.add(onEvent);
    if (registry.streamState !== null) {
        try { onEvent(registry.streamState); } catch { /* the subscriber's own problem */ }
    }
    return () => {
        registry.streamListeners.delete(onEvent);
    };
}

/**
 * The stream as what a stream handler takes: every frame, a `Reopened` for each connection
 * after the first thing the stream reports — the gap before it may have carried a change frame
 * nobody saw — and a `Down` once it has been gone past its grace period.
 */
export function subscribeStreamInputs(onInput: (input: StreamInput) => void): Unsubscribe {
    let hasHeardFromStream = false;
    const unsubscribeFrames = subscribeNotifications((frame) => {
        onInput({ kind: StreamInputKind.Frame, frame });
    });
    const unsubscribeEvents = subscribeStreamEvents((event) => {
        const isFirstMessage = !hasHeardFromStream;
        hasHeardFromStream = true;
        switch (event) {
            case StreamEvent.Down:
                onInput({ kind: StreamInputKind.Down });
                break;
            case StreamEvent.Open:
                if (!isFirstMessage) {
                    onInput({ kind: StreamInputKind.Reopened });
                }
                break;
            default:
                break;
        }
    });
    return () => {
        unsubscribeEvents();
        unsubscribeFrames();
    };
}
